import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Modal,
  Button,
  Typography,
  Icon,
  Radio,
  Card,
} from "@plum-xoxo/admin-dsm";
import styled from "styled-components";

const StyledCard = styled(Card).attrs()`
  border: 1px solid
    ${(props) =>
      props.checked
        ? props.theme.palette.primary.c02
        : props.theme.palette.light.c300};
`;

const ThumbsUpIcon = (props) => (
  <svg
    width={props.width || "20"}
    height={props.height || "20"}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M1.04169 16.25V9.58333C1.04169 8.7779 1.69462 8.125 2.50002 8.125H5.83335C6.17853 8.125 6.45835 8.40482 6.45835 8.75C6.45835 9.09518 6.17853 9.375 5.83335 9.375H2.50002C2.38496 9.375 2.29169 9.46827 2.29169 9.58333V16.25C2.29169 16.365 2.38499 16.4583 2.50002 16.4583H5.83335C6.17853 16.4583 6.45835 16.7382 6.45835 17.0833C6.45835 17.4285 6.17853 17.7083 5.83335 17.7083H2.50002C1.69456 17.7083 1.04169 17.0553 1.04169 16.25Z"
      fill="#4EAE8A"
    />
    <path
      d="M11.5967 2.29199C11.7637 2.29204 11.9237 2.35882 12.0411 2.47754L12.1005 2.53776C13.4857 3.94009 13.8728 6.05787 13.0787 7.86654L12.9647 8.12533H16.6854C18.2601 8.12536 19.3422 9.69289 18.8306 11.1722L17.1013 16.1722C16.7843 17.0885 15.9262 17.7087 14.9561 17.7087H5.83337C5.4882 17.7087 5.20837 17.4288 5.20837 17.0837V8.75033C5.20838 8.58589 5.27347 8.42785 5.38904 8.31087L11.1516 2.47754L11.2476 2.3986C11.35 2.32967 11.4714 2.29198 11.5967 2.29199ZM6.45837 9.00667V16.4587H14.9561C15.3853 16.4587 15.7741 16.1838 15.9196 15.7637L17.649 10.7637C17.8869 10.0757 17.3793 9.37536 16.6854 9.37533H12.0085C11.7975 9.37533 11.6007 9.26875 11.4852 9.09212C11.3698 8.9154 11.3507 8.69212 11.4356 8.49886L11.9344 7.36442C12.4487 6.19326 12.2935 4.84844 11.5658 3.83659L6.45837 9.00667Z"
      fill="#4EAE8A"
    />
  </svg>
);

const RewardTypeModal = ({ isOpen, onClose, cardData }) => {
  const history = useHistory();
  const [selectedRewardType, setSelectedRewardType] = useState("reward-codes");

  // Base reward types configuration
  const baseRewardTypes = [
    {
      id: "reward-codes",
      title: (selectedPayment) => (
        <>
          <Typography.H3
            component="span"
            color={selectedPayment === "reward-codes" ? "primary" : "dark"}
            shade={selectedPayment === "reward-codes" ? "c02" : "c300"}
          >
            Reward Codes
          </Typography.H3>
        </>
      ),
      description: "Send rewards in the form of codes to your recipients.",
      recommended: false,
      url: "/admin/xoxo-code/",
      value: "codes",
    },
    {
      id: "reward-links",
      title: (selectedPayment) => (
        <>
          <Typography.H3
            component="span"
            color={selectedPayment === "reward-links" ? "primary" : "dark"}
            shade={selectedPayment === "reward-links" ? "c02" : "c300"}
          >
            Reward Links
          </Typography.H3>
        </>
      ),
      description: "Send rewards in the form of links to your recipients.",
      recommended: false,
      url: "/admin/xoxo-link/",
      value: "links",
    },
    {
      id: "reward-points",
      title: (selectedPayment) => (
        <>
          <Typography.H3
            component="span"
            color={selectedPayment === "reward-points" ? "primary" : "dark"}
            shade={selectedPayment === "reward-points" ? "c02" : "c300"}
          >
            Reward Points
          </Typography.H3>
        </>
      ),
      description: "Send rewards in the form of points to your recipients.",
      recommended: false,
      url: "/admin/xoxo-points/",
      value: "points",
    },
  ];

  // State for available reward types
  const [rewardTypes, setRewardTypes] = useState(baseRewardTypes);

  // Get available reward types based on cardData
  const getAvailableRewardTypes = () => {
    if (!cardData?.reward_type || !Array.isArray(cardData.reward_type)) {
      return baseRewardTypes;
    }

    // Create a map for quick lookup
    const typeMap = {};
    baseRewardTypes.forEach((type) => {
      typeMap[type.value] = { ...type };
    });

    // Build available types in the order they appear in cardData.reward_type
    const availableTypes = cardData.reward_type
      .map((value) => typeMap[value])
      .filter((type) => type !== undefined);

    // Mark the first item in the array as recommended
    if (availableTypes.length > 0) {
      availableTypes[0].recommended = true;
    }

    return availableTypes;
  };

  // Update reward types when cardData changes
  useEffect(() => {
    const availableTypes = getAvailableRewardTypes();
    setRewardTypes(availableTypes);

    // Set the first available type as selected by default
    if (availableTypes.length > 0) {
      setSelectedRewardType(availableTypes[0].id);
    }
  }, [cardData]);

  // Auto-redirect if only one reward type is available
  useEffect(() => {
    if (isOpen && rewardTypes.length === 1) {
      const singleType = rewardTypes[0];
      const params = new URLSearchParams();
      if (cardData?.template_id) {
        params.append("template_id", cardData.template_id);
      }
      if (cardData?.campaign_id) {
        params.append("campaign_id", cardData.campaign_id);
      }
      const urlWithParams = params.toString()
        ? `${singleType.url}?${params.toString()}`
        : singleType.url;
      history.push(urlWithParams);
      onClose();
    }
  }, [isOpen, rewardTypes, history, onClose, cardData]);

  const handleRewardTypeChange = (rewardTypeId) => {
    setSelectedRewardType(rewardTypeId);
  };

  const handleContinue = () => {
    const selectedType = rewardTypes.find(
      (type) => type.id === selectedRewardType
    );
    if (selectedType) {
      // Navigate to the selected reward type page
      const params = new URLSearchParams();
      if (cardData?.template_id) {
        params.append("template_id", cardData.template_id);
      }
      if (cardData?.campaign_id) {
        params.append("campaign_id", cardData.campaign_id);
      }
      const urlWithParams = params.toString()
        ? `${selectedType.url}?${params.toString()}`
        : selectedType.url;
      history.push(urlWithParams);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Don't render modal if there's only one reward type (auto-redirect handles it)
  if (rewardTypes.length <= 1) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      disableCloseButton={false}
      style={{ width: "600px" }}
    >
      <Typography.H2 color="dark" shade="c300" className="mb-20">
        Select Reward Type
      </Typography.H2>

      <div>
        {rewardTypes.map((rewardType) => (
          <StyledCard
            key={rewardType.id}
            className="mt-16 d-flex align-items-start cursor-pointer"
            spaceAround={6}
            shadow={false}
            checked={selectedRewardType === rewardType.id}
            onClick={() => handleRewardTypeChange(rewardType.id)}
          >
            <div>
              <Radio
                checked={selectedRewardType === rewardType.id}
                onChange={() => handleRewardTypeChange(rewardType.id)}
                value={rewardType.id}
                label={""}
                description=""
                className="mt-2"
              />
            </div>
            <div className="ml-12">
              <Typography.ButtonText color="dark" shade="c300" className="">
                {rewardType.title(selectedRewardType)}
              </Typography.ButtonText>
              <Typography.BodySmall color="dark" shade="c200" className="mt-4">
                {rewardType.description}
              </Typography.BodySmall>
              {rewardType.recommended && (
                <div className="d-flex align-items-center mt-16">
                  <ThumbsUpIcon />
                  <Typography.HelperText
                    color="green"
                    shade="c300"
                    className="ml-8"
                  >
                    Recommended for your use cases.
                  </Typography.HelperText>
                </div>
              )}
            </div>
          </StyledCard>
        ))}
      </div>
      <div className="d-flex justify-content-end w-100 mt-28">
        <Button variant="plain" onClick={handleCancel} className="mr-12">
          Cancel
        </Button>
        <Button variant="fill" onClick={handleContinue}>
          Continue
        </Button>
      </div>
    </Modal>
  );
};

export default RewardTypeModal;
